import type { ExamCategory } from "./types"

/**
 * Domain weights for a PA / health-sciences program exam library.
 * Inspired by PANCE content allocation and cross-cutting clinical domains.
 *
 * @see docs/realistic-school-exam-categories.md
 */
export const DEFAULT_PARENT_WEIGHTS: Record<string, number> = {
  "infectious-disease": 7,
  cardiology: 11,
  pulmonology: 9,
  pharmacology: 14,
  "diagnostic-reasoning": 10,
  "laboratory-interpretation": 8,
  "patient-safety": 5,
  "professionalism-ethics": 5,
  "patient-communication": 4,
  "emergency-recognition": 7,
  pediatrics: 6,
  "womens-health": 7,
  endocrinology: 6,
  neurology: 5,
}

type SkillDef = {
  name: string
  id?: string
  /** Optional learning targets (level 3) and detail values (level 4). */
  targets?: Array<{
    name: string
    id?: string
    details?: string[]
  }>
}

type DomainDef = {
  id: string
  name: string
  description?: string
  skills: Array<string | SkillDef>
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

function buildCategories(domains: DomainDef[]): ExamCategory[] {
  const categories: ExamCategory[] = []

  domains.forEach((domain, domainIndex) => {
    categories.push({
      id: domain.id,
      name: domain.name,
      parentId: null,
      description: domain.description,
      sortOrder: domainIndex,
      isActive: true,
    })

    domain.skills.forEach((skill, skillIndex) => {
      const skillDef: SkillDef =
        typeof skill === "string" ? { name: skill } : skill
      const skillId = skillDef.id ?? `${domain.id}-${slug(skillDef.name)}`
      categories.push({
        id: skillId,
        name: skillDef.name,
        parentId: domain.id,
        sortOrder: skillIndex,
        isActive: true,
      })

      skillDef.targets?.forEach((target, targetIndex) => {
        const targetId = target.id ?? `${skillId}-${slug(target.name)}`
        categories.push({
          id: targetId,
          name: target.name,
          parentId: skillId,
          sortOrder: targetIndex,
          isActive: true,
        })

        target.details?.forEach((detail, detailIndex) => {
          categories.push({
            id: `${targetId}-${slug(detail)}`,
            name: detail,
            parentId: targetId,
            sortOrder: detailIndex,
            isActive: true,
          })
        })
      })
    })
  })

  return categories
}

/** Nursing program category library. Domains stay clinical. Skills are nursing actions, not a diagnosis and treatment list. */
const DOMAIN_LIBRARY: DomainDef[] = [
  {
    id: "infectious-disease",
    name: "Infectious Disease and Microbiology",
    description:
      "Infection cues, transmission precautions, escalation, safe therapy, and prevention.",
    skills: [
      { name: "Recognize infection cues", id: "infectious-disease-identify-the-organism" },
      { name: "Apply transmission precautions", id: "understand-transmission" },
      { name: "Recognize an infection pattern", id: "infectious-disease-recognize-disease-patterns" },
      { name: "Escalate a suspected infection", id: "infectious-disease-diagnose-infection" },
      { name: "Give anti-infective therapy safely", id: "infectious-disease-treat-infection" },
      { name: "Prevent infection", id: "infectious-disease-prevent-infection" },
    ],
  },
  {
    id: "cardiology",
    name: "Cardiovascular Conditions",
    description:
      "Cardiovascular cues, assessment findings, priority nursing care, and the response to that care.",
    skills: [
      { name: "Recognize cardiovascular cues", id: "cardiology-recognize-cardiovascular-conditions" },
      { name: "Interpret assessment findings", id: "cardiology-interpret-cardiovascular-findings" },
      { name: "Prioritize the nursing action", id: "cardiology-diagnose-cardiovascular-disease" },
      { name: "Evaluate the client's response", id: "cardiology-treat-cardiovascular-disease" },
    ],
  },
  {
    id: "pulmonology",
    name: "Pulmonary Conditions",
    skills: [
      { name: "Recognize respiratory cues", id: "pulmonology-recognize-pulmonary-conditions" },
      { name: "Interpret oxygenation findings", id: "pulmonology-interpret-pulmonary-findings" },
      { name: "Prioritize respiratory care", id: "pulmonology-diagnose-pulmonary-disease" },
      { name: "Evaluate the respiratory response", id: "pulmonology-treat-pulmonary-disease" },
    ],
  },
  {
    id: "pharmacology",
    name: "Pharmacology and Therapeutics",
    description:
      "Right medication, how it works, adverse effects, holds, interactions, and client teaching.",
    skills: [
      { name: "Give the right medication", id: "pharmacology-select-the-appropriate-medication" },
      { name: "Explain how the drug works", id: "pharmacology-understand-drug-mechanism" },
      { name: "Recognize adverse effects", id: "pharmacology-recognize-adverse-effects" },
      { name: "Hold for a contraindication", id: "pharmacology-identify-contraindications" },
      { name: "Recognize a drug interaction", id: "pharmacology-identify-drug-interactions" },
      { name: "Clarify a dose change", id: "pharmacology-adjust-medication" },
      { name: "Teach the client about the medication", id: "pharmacology-patient-medication-counseling" },
    ],
  },
  {
    id: "diagnostic-reasoning",
    name: "Diagnostic Reasoning",
    description:
      "Priority problem, clustered cues, the next assessment, and the next nursing action.",
    skills: [
      { name: "Identify the priority problem", id: "diagnostic-reasoning-identify-the-key-clinical-problem" },
      { name: "Cluster the assessment cues", id: "diagnostic-reasoning-build-a-differential-diagnosis" },
      { name: "Choose the next assessment", id: "diagnostic-reasoning-select-the-next-best-test" },
      { name: "Interpret the findings", id: "diagnostic-reasoning-interpret-results" },
      { name: "Decide the next nursing action", id: "diagnostic-reasoning-determine-the-next-best-step" },
    ],
  },
  {
    id: "laboratory-interpretation",
    name: "Laboratory Interpretation",
    skills: [
      { name: "Interpret the complete blood count", id: "laboratory-interpretation-complete-blood-count" },
      { name: "Interpret electrolytes", id: "laboratory-interpretation-electrolytes" },
      { name: "Interpret renal function", id: "laboratory-interpretation-renal-function" },
      { name: "Interpret liver function", id: "laboratory-interpretation-liver-function" },
      { name: "Interpret acid-base balance", id: "laboratory-interpretation-acid-base-interpretation" },
    ],
  },
  {
    id: "patient-safety",
    name: "Patient Safety",
    skills: [
      { name: "Recognize a safety risk", id: "patient-safety-recognize-safety-risk" },
      { name: "Respond to a safety event", id: "patient-safety-respond-to-a-safety-event" },
      { name: "Prevent a medication error", id: "patient-safety-prevent-medical-errors" },
    ],
  },
  {
    id: "professionalism-ethics",
    name: "Professionalism and Ethics",
    skills: [
      { name: "Protect confidentiality", id: "professionalism-ethics-confidentiality" },
      { name: "Confirm informed consent", id: "professionalism-ethics-informed-consent" },
      { name: "Keep professional boundaries", id: "professionalism-ethics-professional-boundaries" },
      { name: "Make an ethical decision", id: "professionalism-ethics-ethical-decision-making" },
      { name: "Document the care", id: "professionalism-ethics-documentation" },
    ],
  },
  {
    id: "patient-communication",
    name: "Patient Communication",
    description:
      "Focused history, explaining the plan, teaching, and difficult conversations.",
    skills: [
      { name: "Collect a focused history", id: "patient-communication-obtain-history" },
      { name: "Explain the plan of care", id: "patient-communication-explain-diagnosis" },
      { name: "Teach the client", id: "patient-communication-counsel-patient" },
      { name: "Hold a difficult conversation", id: "patient-communication-difficult-conversations" },
    ],
  },
  {
    id: "emergency-recognition",
    name: "Emergency Recognition",
    description:
      "Unstable clients, time-critical changes, and the first nursing action.",
    skills: [
      { name: "Recognize an unstable client", id: "emergency-recognition-identify-unstable-patient" },
      { name: "Recognize a time-critical change", id: "emergency-recognition-recognize-time-critical-conditions" },
      { name: "Take the first nursing action", id: "emergency-recognition-select-immediate-action" },
    ],
  },
  {
    id: "pediatrics",
    name: "Pediatric Conditions",
    skills: [
      { name: "Recognize an age-specific cue", id: "pediatrics-recognize-age-specific-presentation" },
      { name: "Apply growth and development", id: "pediatrics-growth-and-development" },
      { name: "Recognize a pediatric infection", id: "pediatrics-pediatric-infection" },
      { name: "Give a pediatric medication safely", id: "pediatrics-pediatric-medication" },
      { name: "Escalate a pediatric red flag", id: "pediatrics-pediatric-red-flags" },
    ],
  },
  {
    id: "womens-health",
    name: "Women's Health",
    skills: [
      { name: "Recognize a common condition", id: "womens-health-identify-common-conditions" },
      { name: "Assess pregnancy warning signs", id: "womens-health-pregnancy" },
      { name: "Teach about contraception", id: "womens-health-contraception" },
      { name: "Recognize an infection concern", id: "womens-health-sti-evaluation" },
    ],
  },
  {
    id: "endocrinology",
    name: "Endocrine Conditions",
    skills: [
      { name: "Manage diabetes cues", id: "endocrinology-diabetes" },
      { name: "Recognize a thyroid change", id: "endocrinology-thyroid" },
      { name: "Recognize an adrenal crisis cue", id: "endocrinology-adrenal" },
    ],
  },
  {
    id: "neurology",
    name: "Neurologic Conditions",
    skills: [
      { name: "Recognize a neurologic cue", id: "neurology-recognize-neurologic-condition" },
      { name: "Interpret a neurologic finding", id: "neurology-localize-neurologic-lesion" },
      { name: "Choose the next neurologic check", id: "neurology-select-neurologic-test" },
      { name: "Escalate a neurologic emergency", id: "neurology-emergency-neurology" },
    ],
  },
]

export const DEFAULT_EXAM_CATEGORIES: ExamCategory[] = buildCategories(DOMAIN_LIBRARY)
